<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Breadcrumbs from '../../Breadcrumbs.svelte';
  import BackButton from '../../BackButton.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import Input from '../../Input.svelte';
  import PaymentsService, { type IncomingPaymentDetails, type OutgoingPayment, type UpdateOutgoingPaymentData } from '../../../lib/api/admin/payments.service';
  import toast from 'svelte-french-toast';
  import { isReceivedPaymentCompleted } from '../../../stores/payment-status.store';

  export let paymentUuid: string = '';

  interface CharityPayment {
    uuid: string;
    charity: {
      uuid: string;
      name: string;
      verified_bsb_no?: string | null;
      verified_acc_no?: string | null;
      verified_acc_name?: string | null;
    };
    paymentReceived: number;
    l2gFeePercentage: number;
    l2gFeeAmount: number;
    paymentToCharity: number;
    status: 'paid' | 'to_make';
    transaction_id: string | null;
    transaction_date: string | null;
    l2gReference: string;
    bsbNo: string | null;
    accNo: string | null;
    accName: string | null;
    isPending?: boolean;
  }

  let isLoading = true;
  let error = '';
  let paymentData: IncomingPaymentDetails | null = null;
  let charityPayments: CharityPayment[] = [];
  let editingPayment: string | null = null;
  let savingPayment: string | null = null;
  let editingCommissionValues: Record<string, number> = {};

  // Computed: Check if all payments are completed
  $: allPaymentsCompleted = charityPayments.length > 0 && charityPayments.every(p => p.status === 'paid');
  
  // Update store when completion status changes
  $: isReceivedPaymentCompleted.set(allPaymentsCompleted);

  // Track previous UUID to reload when it changes
  let previousUuid = '';
  $: if (paymentUuid && paymentUuid !== previousUuid) {
    previousUuid = paymentUuid;
    loadPaymentDetails();
  }

  // Breadcrumbs items
  $: breadcrumbsItems = [
    { label: 'Admin', href: '/admin/dashboard' },
    { label: 'Pending Payouts', href: '/admin/pending-payouts' },
    { label: paymentData ? `Payment - ${paymentData.employer.name}` : 'Payment Detail' }
  ];

  async function loadPaymentDetails() {
    if (!paymentUuid) {
      error = 'Payment UUID is required';
      isLoading = false;
      return;
    }

    try {
      isLoading = true;
      error = '';

      // Load incoming payment details
      const incomingResponse = await PaymentsService.getIncomingPaymentDetails(paymentUuid);
      if (!incomingResponse.success || !incomingResponse.data) {
        error = incomingResponse.message || 'Failed to load payment details';
        toast.error(error);
        return;
      }

      paymentData = incomingResponse.data;
      const details = incomingResponse.data;

      // Load outgoing payments for this employer payment
      // Backend limits per_page to 100, so we need to paginate
      let allOutgoingPayments: OutgoingPayment[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const outgoingResponse = await PaymentsService.getOutgoingPayments({ page, per_page: 100 });
        if (outgoingResponse.success && outgoingResponse.data) {
          const items = Array.isArray(outgoingResponse.data) ? outgoingResponse.data : (outgoingResponse.data.items || []);
          allOutgoingPayments = [...allOutgoingPayments, ...items];
          
          const totalPages = outgoingResponse.paging?.last_page || 1;
          hasMore = page < totalPages;
          page++;
        } else {
          hasMore = false;
        }
      }
      
      // Filter outgoing payments for this employer payment and group by charity
      const paymentsForThisEmployer = allOutgoingPayments.filter(
        p => p.employer_payment_uuid === paymentUuid
      );

      // Create charity payments from charity groups
      charityPayments = details.charities.map(charityGroup => {
        // Find existing outgoing payment for this charity (both paid and to_make)
        const existingPayment = paymentsForThisEmployer.find(
          p => p.charity.uuid === charityGroup.charity.uuid
        );

        const paymentReceived = charityGroup.totalAmount;
        const l2gFeePercentage = existingPayment?.l2g_fee_percentage ?? 2.5;
        const l2gFeeAmount = existingPayment?.l2g_fee_amount ?? (paymentReceived * l2gFeePercentage / 100);
        const paymentToCharity = paymentReceived - l2gFeeAmount;

        const l2gReference = generateL2GReference(paymentUuid, charityGroup.charity.uuid);

        // Check if payment exists (has a real UUID, not a generated one)
        // If type is 'to_make', it might be a generated UUID, so we need to check differently
        const hasRealPayment = existingPayment && existingPayment.type === 'paid';
        const hasPaymentToMake = existingPayment && existingPayment.type === 'to_make';

        // Get bank account details (prefer payout-specific, fallback to charity verified)
        const bsbNo = existingPayment?.payout_bsb_no ?? charityGroup.charity.verified_bsb_no ?? null;
        const accNo = existingPayment?.payout_acc_no ?? charityGroup.charity.verified_acc_no ?? null;
        const accName = existingPayment?.payout_acc_name ?? charityGroup.charity.verified_acc_name ?? null;

        return {
          uuid: existingPayment?.uuid || `pending-${charityGroup.charity.uuid}`,
          charity: charityGroup.charity,
          paymentReceived,
          l2gFeePercentage,
          l2gFeeAmount,
          paymentToCharity,
          status: hasRealPayment ? 'paid' : 'to_make',
          transaction_id: existingPayment?.transaction_id || null,
          transaction_date: existingPayment?.transaction_date || null,
          l2gReference,
          bsbNo,
          accNo,
          accName,
          isPending: !hasRealPayment && !hasPaymentToMake, // True if payment doesn't exist yet
        };
      });
    } catch (err: any) {
      error = err.message || 'An error occurred while loading payment details';
      toast.error(error);
    } finally {
      isLoading = false;
    }
  }

  function formatCurrency(amount: number | string): string {
    return `$${parseFloat(String(amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  function generateL2GReference(paymentUuid: string, charityUuid: string): string {
    // Generate reference: L2G-{8chars} (combines payment and charity UUIDs for uniqueness)
    // Take first 4 chars from payment UUID and first 4 chars from charity UUID
    const combined = (paymentUuid.replace(/-/g, '') + charityUuid.replace(/-/g, '')).substring(0, 8).toUpperCase();
    return `L2G-${combined}`;
  }

  function copyToClipboard(text: string, label: string = 'Reference') {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error(`Failed to copy ${label.toLowerCase()}`);
    });
  }

  function handleBack() {
    push('/admin/pending-payouts');
  }

  function startEditing(charityUuid: string) {
    editingPayment = charityUuid;
    const payment = charityPayments.find(p => p.charity.uuid === charityUuid);
    if (payment) {
      editingCommissionValues[charityUuid] = payment.l2gFeePercentage;
    }
  }

  function cancelEditing() {
    editingPayment = null;
    editingCommissionValues = {};
  }

  function updateL2GFee(charityUuid: string, percentage: number) {
    editingCommissionValues[charityUuid] = percentage;
    const paymentIndex = charityPayments.findIndex(p => p.charity.uuid === charityUuid);
    if (paymentIndex === -1) return;

    // Update the payment values
    const payment = charityPayments[paymentIndex];
    const l2gFeeAmount = (payment.paymentReceived * percentage) / 100;
    const paymentToCharity = payment.paymentReceived - l2gFeeAmount;

    // Reassign to trigger reactivity
    charityPayments[paymentIndex] = {
      ...payment,
      l2gFeePercentage: percentage,
      l2gFeeAmount: l2gFeeAmount,
      paymentToCharity: paymentToCharity,
    };
    
    // Reassign the array to ensure Svelte detects the change
    charityPayments = charityPayments;
  }

  function handleCommissionInput(charityUuid: string, event: Event) {
    const target = event.target as HTMLInputElement;
    const value = parseFloat(target.value) || 0;
    updateL2GFee(charityUuid, value);
  }

  async function markAsPaid(charityUuid: string) {
    const payment = charityPayments.find(p => p.charity.uuid === charityUuid);
    if (!payment || !paymentData) return;

    try {
      savingPayment = charityUuid;
      
      const updateData: UpdateOutgoingPaymentData = {
        l2g_fee_percentage: payment.l2gFeePercentage,
        l2g_fee_amount: payment.l2gFeeAmount,
        mark_as_paid: true,
        transaction_id: payment.transaction_id || undefined,
        transaction_date: payment.transaction_date || undefined,
      };

      // If payment doesn't exist yet, include required fields for creation
      // Payments with status 'to_make' or 'pending-' UUID don't exist in DB yet
      const isNewPayment = payment.status === 'to_make' || payment.uuid.startsWith('pending-') || payment.isPending;
      
      // Always include creation fields - backend will use them if payment doesn't exist
      updateData.employer_payment_uuid = paymentUuid;
      updateData.charity_uuid = payment.charity.uuid;
      updateData.amount = payment.paymentReceived;

      // Use the payment UUID if it exists, otherwise use placeholder
      // Backend will detect if payment doesn't exist and create it using the provided fields
      const paymentUuidToUse = isNewPayment ? '00000000-0000-0000-0000-000000000000' : payment.uuid;
      
      const response = await PaymentsService.updateOutgoingPayment(paymentUuidToUse, updateData);

      if (response.success) {
        payment.status = 'paid';
        editingPayment = null;
        toast.success(`Payment to ${payment.charity.name} marked as paid`);
        await loadPaymentDetails();
        
        // Check if all payments are now completed (after reload)
        // Small delay to ensure charityPayments array is updated
        setTimeout(() => {
          const allCompleted = charityPayments.length > 0 && charityPayments.every(p => p.status === 'paid');
          if (allCompleted) {
            toast.success('All payments completed! This payment will now appear in the Completed list.');
          }
        }, 300);
      } else {
        const errorMsg = response.message || 'Failed to mark payment as paid';
        console.error('Update payment error:', response);
        toast.error(errorMsg);
      }
    } catch (err: any) {
      console.error('Mark as paid error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to mark payment as paid';
      toast.error(errorMsg);
    } finally {
      savingPayment = null;
    }
  }

  async function savePayment(charityUuid: string) {
    const payment = charityPayments.find(p => p.charity.uuid === charityUuid);
    if (!payment) return;

    try {
      savingPayment = charityUuid;

      // If it's a pending payment, we can't save it yet - it needs to be created via the backend
      // The backend creates payments automatically when marking as paid
      if (payment.uuid.startsWith('pending-') || payment.isPending) {
        // For pending payments, we can't save without creating first
        // The mark_as_paid flow will handle creation
        toast('Saving payment settings. Use "Mark as Paid" to create and complete the payment.');
        editingPayment = null;
        return;
      }

      const updateData: UpdateOutgoingPaymentData = {
        l2g_fee_percentage: payment.l2gFeePercentage,
        l2g_fee_amount: payment.l2gFeeAmount,
      };

      const response = await PaymentsService.updateOutgoingPayment(payment.uuid, updateData);

      if (response.success) {
        editingPayment = null;
        toast.success(`Payment to ${payment.charity.name} updated`);
        // Reload to get updated data
        await loadPaymentDetails();
      } else {
        const errorMsg = response.message || 'Failed to update payment';
        console.error('Save payment error:', response);
        toast.error(errorMsg);
      }
    } catch (err: any) {
      console.error('Save payment error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update payment';
      toast.error(errorMsg);
    } finally {
      savingPayment = null;
    }
  }

  async function downloadReceipt(charityUuid: string) {
    const payment = charityPayments.find(p => p.charity.uuid === charityUuid);
    if (!payment || payment.status !== 'paid') {
      toast.error('Receipt is only available for paid payments');
      return;
    }

    try {
      // Use the payment UUID (DonationPayout UUID) to download receipt
      const { api } = await import('../../../lib/api/client');
      const response = await api.get<Blob>(`/admin/payments/outgoing/${payment.uuid}/receipt`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${payment.charity.name}-${payment.transaction_date || 'receipt'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Receipt downloaded successfully');
    } catch (err: any) {
      console.error('Download receipt error:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to download receipt');
    }
  }

  onMount(() => {
    // Reset completion status when component mounts
    isReceivedPaymentCompleted.set(false);
    
    if (paymentUuid) {
      previousUuid = paymentUuid;
      loadPaymentDetails();
    } else {
      error = 'Payment UUID is required';
      isLoading = false;
    }
  });
</script>

<div class="received-payment-detail-page">
  <Breadcrumbs items={breadcrumbsItems} />
  <BackButton text="Back to Pending Payouts" onClick={handleBack} />

  {#if isLoading}
    <div style="min-height: 400px; padding: 2rem;">
      <LoadingSkeleton lines={8} height="2rem" />
    </div>
  {:else if error}
    <ErrorState
      message="Failed to load payment details"
      description={error}
      onRetry={loadPaymentDetails}
    />
  {:else if paymentData}
    <!-- Payment Header Section -->
    <div class="payment-header">
      <div class="header-info">
        <h1 class="payment-title">
          {#if paymentData.invoice_number}
            Invoice #{paymentData.invoice_number}
          {:else}
            Payment Detail
          {/if}
        </h1>
        <div class="payment-meta">
          {#if allPaymentsCompleted}
            <span class="meta-badge status-completed">Completed</span>
          {:else}
            <span class="meta-badge status-received">Received</span>
          {/if}
          {#if paymentData.payment_date}
            <span class="meta-separator">•</span>
            <span class="meta-item">Received on {formatDate(paymentData.payment_date)}</span>
          {/if}
          {#if paymentData.reference_number}
            <span class="meta-separator">•</span>
            <span class="meta-item">Ref: {paymentData.reference_number}</span>
          {/if}
        </div>
        <div class="payment-summary">
          <span>{paymentData.totalDonations} donation{paymentData.totalDonations !== 1 ? 's' : ''}</span>
          <span class="separator">•</span>
          <span>{paymentData.totalHours.toFixed(1)} hours</span>
          <span class="separator">•</span>
          <span>{paymentData.totalCharities} charities</span>
          <span class="separator">•</span>
          <span class="total-amount">Total: {formatCurrency(paymentData.totalAmount)}</span>
        </div>
      </div>
    </div>

    <!-- Employer Details Card -->
    <div class="employer-details-card">
      <div class="card-header">
        <h2>Employer Information</h2>
      </div>
      <div class="card-content">
        <div class="details-grid">
          <div class="detail-item">
            <span class="detail-label">Employer Name</span>
            <span class="detail-value">{paymentData.employer.name}</span>
          </div>
          {#if paymentData.employer.abn}
            <div class="detail-item">
              <span class="detail-label">ABN</span>
              <span class="detail-value">{paymentData.employer.abn}</span>
            </div>
          {/if}
          {#if paymentData.employer.contact_name}
            <div class="detail-item">
              <span class="detail-label">Contact Name</span>
              <span class="detail-value">{paymentData.employer.contact_name}</span>
            </div>
          {/if}
          {#if paymentData.employer.contact_email}
            <div class="detail-item">
              <span class="detail-label">Contact Email</span>
              <span class="detail-value">
                <a href="mailto:{paymentData.employer.contact_email}" class="email-link">
                  {paymentData.employer.contact_email}
                </a>
              </span>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Charity Payments Section -->
    <div class="charity-payments-section">
      <h2 class="section-title">Charity Payments</h2>
      {#each charityPayments as charityPayment (charityPayment.charity.uuid)}
        <div class="charity-payment-card" class:paid={charityPayment.status === 'paid'}>
          <div class="charity-payment-header">
            <div class="charity-info">
              <h3 class="charity-name">
                {charityPayment.charity.name}
                {#if charityPayment.status === 'paid'}
                  <span class="status-inline">(PAID)</span>
                  {#if charityPayment.transaction_date}
                    <span class="paid-date-inline"> - {formatDate(charityPayment.transaction_date)}</span>
                  {/if}
                {:else}
                  <span class="status-inline status-pending-inline">(PENDING)</span>
                {/if}
              </h3>
            </div>
            {#if charityPayment.status === 'paid'}
              <Button variant="secondary" size="small" on:click={() => downloadReceipt(charityPayment.charity.uuid)}>
                Download Receipt
              </Button>
            {/if}
          </div>

          <div class="charity-payment-content">
            {#if editingPayment === charityPayment.charity.uuid}
              <!-- Edit Mode -->
              <div class="payment-form">
                <div class="form-row">
                  <div class="form-group">
                    <span class="field-label">Payment Received</span>
                    <div class="readonly-value">{formatCurrency(charityPayment.paymentReceived)}</div>
                  </div>
                  <div class="form-group">
                    <label class="field-label" for="transaction-fee-{charityPayment.charity.uuid}">Transaction Fee (%)</label>
                    <Input
                      id="transaction-fee-{charityPayment.charity.uuid}"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={(editingCommissionValues[charityPayment.charity.uuid] ?? charityPayment.l2gFeePercentage).toString()}
                      on:input={(e) => handleCommissionInput(charityPayment.charity.uuid, e)}
                    />
                  </div>
                  <div class="form-group">
                    <span class="field-label">Transaction Fee Amount</span>
                    <div class="readonly-value">{formatCurrency(charityPayment.l2gFeeAmount)}</div>
                  </div>
                  <div class="form-group">
                    <span class="field-label">Payment to Charity</span>
                    <div class="readonly-value payment-to-charity">{formatCurrency(charityPayment.paymentToCharity)}</div>
                  </div>
                  <div class="form-group full-width">
                    <span class="field-label">Payment Reference</span>
                    <div class="reference-field">
                      <div class="readonly-value reference-value">{charityPayment.l2gReference}</div>
                      <Button variant="secondary" size="small" on:click={() => copyToClipboard(charityPayment.l2gReference)}>
                        Copy
                      </Button>
                    </div>
                    <div class="reference-note">Use this reference in your bank transfer</div>
                  </div>
                  {#if charityPayment.bsbNo}
                  <div class="form-group full-width">
                    <span class="field-label">BSB</span>
                    <div class="reference-field">
                      <div class="readonly-value reference-value">{charityPayment.bsbNo}</div>
                      <Button variant="secondary" size="small" on:click={() => copyToClipboard(charityPayment.bsbNo || '')}>
                        Copy
                      </Button>
                    </div>
                  </div>
                  {/if}
                  {#if charityPayment.accNo}
                  <div class="form-group full-width">
                    <span class="field-label">Account Number</span>
                    <div class="reference-field">
                      <div class="readonly-value reference-value">{charityPayment.accNo}</div>
                      <Button variant="secondary" size="small" on:click={() => copyToClipboard(charityPayment.accNo || '')}>
                        Copy
                      </Button>
                    </div>
                  </div>
                  {/if}
                  {#if charityPayment.accName}
                  <div class="form-group full-width">
                    <span class="field-label">Account Name</span>
                    <div class="reference-field">
                      <div class="readonly-value reference-value">{charityPayment.accName}</div>
                      <Button variant="secondary" size="small" on:click={() => copyToClipboard(charityPayment.accName || '')}>
                        Copy
                      </Button>
                    </div>
                  </div>
                  {/if}
                </div>
                <div class="form-actions">
                  <Button variant="secondary" on:click={cancelEditing} disabled={savingPayment === charityPayment.charity.uuid}>
                    Cancel
                  </Button>
                  {#if charityPayment.status !== 'paid'}
                    <Button variant="primary" on:click={() => markAsPaid(charityPayment.charity.uuid)} disabled={savingPayment === charityPayment.charity.uuid}>
                      {savingPayment === charityPayment.charity.uuid ? 'Processing...' : 'Mark as Paid'}
                    </Button>
                  {/if}
                </div>
              </div>
            {:else}
              <!-- View Mode -->
              <div class="payment-summary-grid">
                <div class="summary-item">
                  <span class="summary-label">Payment Received</span>
                  <span class="summary-value">{formatCurrency(charityPayment.paymentReceived)}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Transaction Fee</span>
                  <span class="summary-value">{charityPayment.l2gFeePercentage}% ({formatCurrency(charityPayment.l2gFeeAmount)})</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Payment to Charity</span>
                  <span class="summary-value payment-to-charity">{formatCurrency(charityPayment.paymentToCharity)}</span>
                </div>
                <div class="summary-item full-width">
                  <span class="summary-label">Payment Reference</span>
                  <div class="reference-field-view">
                    <span class="summary-value reference-value-view">{charityPayment.l2gReference}</span>
                    <Button variant="secondary" size="small" on:click={() => copyToClipboard(charityPayment.l2gReference, 'L2G Payment Reference')}>
                      Copy
                    </Button>
                  </div>
                  <div class="reference-note">Use this reference in your bank transfer</div>
                </div>
                {#if charityPayment.bsbNo}
                <div class="summary-item full-width">
                  <span class="summary-label">BSB</span>
                  <div class="reference-field-view">
                    <span class="summary-value reference-value-view">{charityPayment.bsbNo}</span>
                    <Button variant="secondary" size="small" on:click={() => copyToClipboard(charityPayment.bsbNo || '', 'BSB')}>
                      Copy
                    </Button>
                  </div>
                </div>
                {/if}
                {#if charityPayment.accNo}
                <div class="summary-item full-width">
                  <span class="summary-label">Account Number</span>
                  <div class="reference-field-view">
                    <span class="summary-value reference-value-view">{charityPayment.accNo}</span>
                    <Button variant="secondary" size="small" on:click={() => copyToClipboard(charityPayment.accNo || '', 'Account Number')}>
                      Copy
                    </Button>
                  </div>
                </div>
                {/if}
                {#if charityPayment.accName}
                <div class="summary-item full-width">
                  <span class="summary-label">Account Name</span>
                  <div class="reference-field-view">
                    <span class="summary-value reference-value-view">{charityPayment.accName}</span>
                    <Button variant="secondary" size="small" on:click={() => copyToClipboard(charityPayment.accName || '', 'Account Name')}>
                      Copy
                    </Button>
                  </div>
                </div>
                {/if}
              </div>
              {#if charityPayment.status !== 'paid'}
                <div class="payment-actions">
                  <Button variant="secondary" on:click={() => startEditing(charityPayment.charity.uuid)}>
                    Edit & Mark as Paid
                  </Button>
                </div>
              {/if}
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .received-payment-detail-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem 2rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .payment-header {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .header-info {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .payment-title {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .payment-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .meta-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .meta-badge.status-received {
    background-color: #d1fae5;
    color: #065f46;
  }

  .meta-badge.status-completed {
    background-color: #d1fae5;
    color: #065f46;
  }

  .meta-separator {
    color: #d1d5db;
  }

  .payment-summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .separator {
    color: #d1d5db;
  }

  .total-amount {
    font-weight: 600;
    color: #111827;
    font-size: 1rem;
  }

  .employer-details-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .card-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .card-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .card-content {
    padding: 1.5rem 2rem;
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .detail-value {
    font-size: 1rem;
    color: #111827;
  }

  .email-link {
    color: #3b82f6;
    text-decoration: none;
  }

  .email-link:hover {
    text-decoration: underline;
  }

  .charity-payments-section {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .charity-payment-card {
    background: #f9fafb;
    border-radius: 8px;
    padding: 1.5rem;
    border: 1px solid #e5e7eb;
    margin-bottom: 1rem;
  }

  .charity-payment-card:last-child {
    margin-bottom: 0;
  }

  .charity-payment-card.paid {
    background: #f0fdf4;
    border-color: #86efac;
  }

  .charity-payment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .charity-info {
    display: flex;
    align-items: center;
    flex: 1;
  }

  .charity-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;
  }

  .charity-payment-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .payment-summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .summary-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .summary-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .summary-value {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
  }

  .summary-value.payment-to-charity {
    color: #059669;
    font-size: 1.125rem;
  }

  .payment-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .payment-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-group label,
  .form-group .field-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .readonly-value {
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    color: #111827;
    font-weight: 500;
  }

  .readonly-value.payment-to-charity {
    color: #059669;
    font-weight: 600;
  }

  .form-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .form-group.full-width {
    grid-column: 1 / -1;
  }

  .reference-field {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .reference-field .readonly-value {
    flex: 1;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  .reference-field-view {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    margin-top: 0.5rem;
  }

  .reference-value-view {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #1e40af;
  }

  .reference-note {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
    font-style: italic;
  }


  .status-inline {
    font-size: 0.875rem;
    font-weight: 600;
    color: #059669;
    margin-left: 0.5rem;
  }

  .status-pending-inline {
    color: #d97706;
  }

  .paid-date-inline {
    font-size: 0.875rem;
    font-weight: 400;
    color: #6b7280;
    margin-left: 0.25rem;
  }
</style>

